/**
 * SchemaCompareSetupDialog - Setup dialog for schema comparison
 *
 * Allows user to configure source and target databases before opening
 * the schema compare tab. Supports optional schema filtering and single-object comparison.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Database, Server, ArrowLeftRight, GitCompare, Info, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import { getDatabaseFromConnectionId } from '@/features/connections/utils/getDatabaseFromConnectionId';
// Deliberately NOT routed through `useDatabaseList` / the shared listing cache
// (phase 1 recorded gap — Docs/ObjectExplorer.md). This dialog needs a listing
// that is genuinely IN FLIGHT: it threads `onPhase` into `ListingLoadingLabel`
// ("Connecting to server…" → "Loading databases…"), and its failure specs pin a
// per-call `__holdListing { afterCall: 2 }` seam where the two panels on the
// SAME connection must produce two DISTINCT calls. Serving either panel from a
// shared cache entry removes both premises.
import {
  getDatabases,
  type DatabasesListingPhase,
  type DatabasesListingState,
} from '@/features/database-objects/services/databasesService';
import { getTables } from '@/features/database-objects/services/objectsService';
import { FallbackSelectContent } from '@/features/connection-diagnostics/components/FallbackSelectContent';
import { DatabaseListingFallbackBanner } from '@/features/connection-diagnostics/components/DatabaseListingFallbackBanner';
import { UnlistedDatabaseNotice } from '@/features/connection-diagnostics/components/UnlistedDatabaseNotice';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { getSchemaCompareDefaultOptions } from '../../../../store/appStore';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { getEngineFromConnectionId } from '../utils/engineUtils';
import { SchemaCompareObjectPicker, type ObjectPickerValue } from './SchemaCompareObjectPicker';
import { SchemaCompareInlineOptions } from './SchemaCompareInlineOptions';
import { SchemaCompareCompactField } from './SchemaCompareCompactField';
import {
  applyEngineDefaults,
  applyStandardDepthFallback,
  ENGINE_PROPERTY_ALLOWLIST,
} from '../utils/compareDepth';
import {
  isFolderEndpoint,
  type SchemaCompareScopeCapKey,
} from '../../core/types/workspaceTypes';
import type {
  SchemaCompareEndpoint,
  SchemaCompareObjectType,
  SchemaCompareOptions,
  SchemaFilter,
} from '../../core/types/workspaceTypes';
import { RecentlyComparedLink, RecentlyComparedSection } from './RecentlyComparedSection';
import { useRecentlyComparedDisclosure } from '@/features/workspace/common/hooks/useRecentlyComparedDisclosure';
import {
  useRecentSchemaCompareStore,
  type RecentSchemaCompareEntry,
} from '../store/recentSchemaCompareStore';
import {
  pickInheritedCaps,
  rememberSchemaCompareOptions,
  resolveDefaultSchemaCompareOptions,
} from '../store/schemaCompareOptionDefaultsStore';

import { SchemaFilterSection } from './SchemaFilterSection';
import { safeConnectionName, safeConnectionServer } from '@/types/safeConnectionDisplay';

type EndpointState = {
  connectionId?: string;
  database?: string;
  databases: string[];
  /** Slice 5b: 'ok' / 'empty' / 'error' from the listing call. */
  databasesState?: DatabasesListingState;
  /**
   * Soporte C3: which phase the in-flight database listing is in, so the
   * field can say "Connecting to server…" vs "Loading databases…". Only
   * meaningful while `isLoadingDatabases` is true.
   */
  databasesPhase?: DatabasesListingPhase;
  schemas: string[];
  /** Soporte C2: `getTables`' `error` string when the schema-derivation listing failed. */
  schemasError?: string;
  isLoadingDatabases: boolean;
  isLoadingSchemas: boolean;
  // Single-object selection
  objectType?: SchemaCompareObjectType;
  objectSchema?: string;
  objectName?: string;
};

type SchemaCompareSetupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill source from context */
  initialSource?: SchemaCompareEndpoint;
  /** Pre-fill target from context */
  initialTarget?: SchemaCompareEndpoint;
  /** Initial compare whole database setting */
  initialCompareWholeDatabase?: boolean;
  /** Initial compare options (depth, object types, ignore lists). */
  initialOptions?: SchemaCompareOptions;
  /**
   * W5.1 fix round 1 (M2) — whether `initialOptions` reflects an actual
   * choice the user made (this dialog's own controls, a picked "Recently
   * compared" entry, or a previous "Save as my defaults") versus an
   * unmodified `createDefaultSchemaCompareContent()` factory baseline a
   * caller is merely passing through (e.g. a tab created directly from
   * Object Explorer, which never opened this dialog first).
   *
   * Defaults to `true` — every current caller (`SchemaCompareTab.tsx`'s
   * "Change" flow) hands back options that already passed through this
   * dialog at some point, so they're user-chosen by definition; omitting
   * this prop reproduces exactly today's behaviour (the auto-seed effect
   * below never runs once `initialOptions` is set).
   *
   * Pass `false` only from a call site that knows its `initialOptions` never
   * reflected user intent. When `false`, a saved per-engine default (if one
   * exists once an engine resolves) REPLACES `initialOptions` outright
   * instead of being permanently blocked by it — see the auto-seed effect.
   */
  optionsAreUserChosen?: boolean;
  /**
   * Which keys in `initialOptions` a Database Blueprint manifest imposed rather
   * than the user choosing them (W3.4/F07). Absent for every ordinary
   * comparison. See `SchemaCompareTabContent.scopeCapKeys`.
   */
  initialScopeCapKeys?: SchemaCompareScopeCapKey[];
  /** Pre-fill schema filter (applies to both endpoints). */
  initialSchemaFilter?: SchemaFilter;
  /**
   * If provided, called with selected endpoints and mode instead of creating a new tab.
   * `schemaFilter` is the unified filter applied to both source and target.
   * `options` is the full SchemaCompareOptions (depth → detailed/ignoredProperties,
   * object-type filters, ignoreCase/ignoreWhitespace, includeSystemSchemas).
   */
  onConfirm?: (
    source: SchemaCompareEndpoint,
    target: SchemaCompareEndpoint,
    compareWholeDatabase: boolean,
    schemaFilter: SchemaFilter | undefined,
    options: SchemaCompareOptions,
  ) => void;
};

type DepthKeys = { detailed: boolean | undefined; ignoredProperties: string[] | undefined };

const depthKeysOf = (options: SchemaCompareOptions): DepthKeys => ({
  detailed: options.detailed,
  ignoredProperties: options.ignoredProperties,
});

/**
 * B90 — rebuild one endpoint from a picked "Recently compared" entry WITHOUT
 * discarding listings the dialog has already loaded and will not reload.
 *
 * The two listing effects are keyed on `[connectionId, engine, open]` and
 * `[connectionId, database, engine, open, isSqlite]`. A recent almost always
 * points at the connection (and often the database) the dialog already holds —
 * re-running yesterday's comparison on the same server is the whole point of
 * the feature — so those dependencies do not change and the effects never
 * re-run. Blanking `databases` / `schemas` here therefore destroyed lists that
 * nothing would ever rebuild, and the damage was user-visible:
 *
 *   - the database combobox's `value` matched no rendered `<SelectItem>`, and
 *     a Radix `<Select>` in that state paints NEITHER the value NOR the
 *     placeholder — the field just went blank and stayed blank (measured on
 *     both endpoints; the showcase slide caught the source side because the
 *     target had already collapsed to its text affordance);
 *   - opening that combobox offered "No databases found", so the restored
 *     database could not even be changed by hand;
 *   - the schema filter's picker lost every schema it could offer.
 *
 * Both caches are keyed by exactly the identity being compared here, so
 * keeping them when the key is unchanged is not a heuristic: it is the same
 * data the effect would have re-fetched. When the key DOES change, the cache
 * is genuinely stale, is cleared, and the effect re-runs because its own
 * dependency changed.
 */
const restoreEndpointFromRecent = (
  prev: EndpointState,
  endpoint: SchemaCompareEndpoint,
): EndpointState => {
  const sameConnection = !!prev.connectionId && prev.connectionId === endpoint.connectionId;
  const sameDatabase = sameConnection && !!prev.database && prev.database === endpoint.database;
  return {
    connectionId: endpoint.connectionId,
    database: endpoint.database,
    databases: sameConnection ? prev.databases : [],
    databasesState: sameConnection ? prev.databasesState : undefined,
    schemas: sameDatabase ? prev.schemas : [],
    schemasError: sameDatabase ? prev.schemasError : undefined,
    isLoadingDatabases: false,
    isLoadingSchemas: false,
    objectType: endpoint.objectType,
    objectSchema: endpoint.objectSchema,
    objectName: endpoint.objectName,
  };
};

const sameDepthKeys = (a: DepthKeys, b: DepthKeys): boolean => {
  if (a.detailed !== b.detailed) return false;
  const x = a.ignoredProperties;
  const y = b.ignoredProperties;
  if (x === y) return true;
  if (!x || !y || x.length !== y.length) return false;
  return x.every((key, index) => key === y[index]);
};

export function SchemaCompareSetupDialog({
  open,
  onOpenChange,
  initialSource,
  initialTarget,
  initialCompareWholeDatabase = true,
  initialOptions,
  optionsAreUserChosen,
  initialScopeCapKeys,
  initialSchemaFilter,
  onConfirm,
}: SchemaCompareSetupDialogProps) {
  const { connections, activeConnections } = useConnectionStore();
  const { addTab } = useWorkspaceStore();
  const recordRecent = useRecentSchemaCompareStore((s) => s.record);
  const { track } = useAnalytics();

  // Compare mode state
  const [compareWholeDatabase, setCompareWholeDatabase] = useState(initialCompareWholeDatabase);

  // Full comparison options (depth, object types, ignore lists, system schemas).
  const [options, setOptions] = useState<SchemaCompareOptions>(initialOptions ?? {});

  // W5.1 (D4) — guards the "seed a fresh dialog session from saved per-engine
  // defaults" effect below so it applies at most once per open session, and
  // never after the caller already supplied explicit options (Change mode,
  // `initialOptions`) or after the user picked a "Recently compared" entry
  // (which owns the options decision for that session instead).
  const optionsSeededRef = useRef(false);

  // Unified schema filter (applies to both source and target)
  const [schemaFilter, setSchemaFilter] = useState<SchemaFilter | undefined>(
    initialSchemaFilter ?? initialSource?.schemaFilter ?? initialTarget?.schemaFilter,
  );

  const includeSystemSchemas = options.includeSystemSchemas ?? false;
  const setIncludeSystemSchemas = useCallback((next: boolean) => {
    setOptions((prev) => ({ ...prev, includeSystemSchemas: next }));
  }, []);

  // Source state
  const [source, setSource] = useState<EndpointState>({
    connectionId: initialSource?.connectionId,
    database: initialSource?.database,
    databases: [],
    schemas: [],
    isLoadingDatabases: false,
    isLoadingSchemas: false,
    objectType: initialSource?.objectType,
    objectSchema: initialSource?.objectSchema,
    objectName: initialSource?.objectName,
  });

  // Target state
  const [target, setTarget] = useState<EndpointState>({
    connectionId: initialTarget?.connectionId,
    database: initialTarget?.database,
    databases: [],
    schemas: [],
    isLoadingDatabases: false,
    isLoadingSchemas: false,
    objectType: initialTarget?.objectType,
    objectSchema: initialTarget?.objectSchema,
    objectName: initialTarget?.objectName,
  });

  // Filter to only show connected connections
  const connectedConnections = useMemo(() => {
    return activeConnections
      .filter((ac) => ac.status === 'connected')
      .map((ac) => ({
        ...ac.connection,
        displayName: safeConnectionName(ac.connection),
      }));
  }, [activeConnections]);

  /**
   * #91 — the collapsed text affordance's label for a connection field.
   *
   * `SchemaCompareCompactField` falls back to the raw `value` when no
   * `displayLabel` is given, and this used to look ONLY in
   * `connectedConnections`. "Recently compared" entries are persisted across
   * restarts, so restoring one routinely names a connection that is saved but
   * not currently open — and the field then rendered its internal id
   * (`conn-b`) as if that were a server name (measured).
   *
   * Saved connections are consulted second so a closed connection still shows
   * its own alias. A connection that is in neither list has been deleted
   * outright; that is the case "Recently compared" already marks
   * "(connection unavailable)" on the row itself, and it still falls through
   * to the raw value here.
   */
  const connectionDisplayLabel = useCallback(
    (connectionId: string | undefined): string | undefined => {
      if (!connectionId) return undefined;
      const conn =
        connectedConnections.find((c) => c.id === connectionId) ??
        connections.find((c) => c.id === connectionId);
      return conn ? safeConnectionName(conn) : undefined;
    },
    [connectedConnections, connections],
  );

  // Get engine types
  const sourceEngine = useMemo(() => {
    return getEngineFromConnectionId(source.connectionId, connections);
  }, [source.connectionId, connections]);

  const targetEngine = useMemo(() => {
    return getEngineFromConnectionId(target.connectionId, connections);
  }, [target.connectionId, connections]);

  const isSqliteSource = sourceEngine === 'sqlite';
  const isSqliteTarget = targetEngine === 'sqlite';

  // W5.2 (D3) — `options` itself only ever holds an explicit depth choice
  // (the user's own toggle, a restored recent, or a saved per-engine
  // default via the auto-seed effect below). A dialog session that has none
  // of those yet must still SHOW and SUBMIT the Standard preset rather than
  // the accidental "Deep" an options object with no depth fields at all
  // infers to — this derived value is a pure read-time fallback, never
  // written back into `options`, so it can never race or fight with the
  // saved-defaults auto-seed effect (see that effect's own comments for why
  // writing this eagerly into state would be unsafe for the M1 scenario).
  const engineForOptions = sourceEngine ?? targetEngine;
  const effectiveOptions = useMemo(
    () => applyStandardDepthFallback(options, engineForOptions),
    [options, engineForOptions],
  );

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      // W5.1 — an explicit `initialOptions` (Change mode, or any future
      // preset-restoring caller) means the caller already made the options
      // decision; the auto-seed effect below must never override it. M2
      // exception: `optionsAreUserChosen === false` marks `initialOptions`
      // as a non-decision (factory baseline) the auto-seed effect is still
      // allowed to seed over once an engine resolves.
      optionsSeededRef.current = !!initialOptions && optionsAreUserChosen !== false;
      setCompareWholeDatabase(initialCompareWholeDatabase);
      setOptions(initialOptions ?? {});
      setSchemaFilter(
        initialSchemaFilter ?? initialSource?.schemaFilter ?? initialTarget?.schemaFilter,
      );
      setSource({
        connectionId: initialSource?.connectionId,
        database: initialSource?.database,
        databases: [],
        schemas: [],
        isLoadingDatabases: false,
        isLoadingSchemas: false,
        objectType: initialSource?.objectType,
        objectSchema: initialSource?.objectSchema,
        objectName: initialSource?.objectName,
      });
      setTarget({
        connectionId: initialTarget?.connectionId,
        database: initialTarget?.database,
        databases: [],
        schemas: [],
        isLoadingDatabases: false,
        isLoadingSchemas: false,
        objectType: initialTarget?.objectType,
        objectSchema: initialTarget?.objectSchema,
        objectName: initialTarget?.objectName,
      });
    }
  }, [
    open,
    initialSource,
    initialTarget,
    initialCompareWholeDatabase,
    initialOptions,
    optionsAreUserChosen,
    initialSchemaFilter,
  ]);

  /**
   * The depth keys (`detailed` + `ignoredProperties`) this dialog itself last
   * seeded — see the engine-change effect below for why "unchanged from what we
   * wrote" is the truthful test for "the user has not chosen a depth".
   */
  const seededDepthRef = useRef<DepthKeys | null>(null);

  /**
   * F07 — a NEW comparison starts from the options the user last ran, not from
   * a blank object.
   *
   * The toolbar, the command palette and "Recently compared" all render this
   * dialog with no `initialOptions`, so its state started at `{}` and every
   * un-ticked object type / ignored property the user had set on their
   * previous compare was silently gone. `initialOptions === undefined` is the
   * truthful discriminator for "the caller has no options for this comparison
   * yet": the Change flow on an existing tab always passes
   * `content.options`, so it never enters this branch and can never be
   * overwritten. (This is why no `optionsAreUserChosen` prop exists — the
   * information is already here and derivable, and a prop no caller can
   * compute truthfully would be free to be wrong.)
   *
   * Declared AFTER the reset effect above on purpose: effects run in
   * declaration order, so the reset's `setOptions(initialOptions ?? {})` is
   * queued first and this seed lands on top of it, not under it.
   *
   * The `Object.keys(prev).length === 0` guard means "still pristine". The
   * engine is only known once a source connection is picked, which can be
   * several interactions after `open` flips — by then the user may already
   * have toggled something, and their choice wins.
   *
   * Once seeded, options are NOT re-seeded when the user switches the source
   * connection to a different engine — their toggles survive. But
   * `ignoredProperties` is an engine-specific key set, so the keys themselves
   * cannot: see the engine-change effect immediately below.
   */
  useEffect(() => {
    if (!open || initialOptions || !sourceEngine) return;
    setOptions((prev) => {
      if (Object.keys(prev).length !== 0) return prev;
      const seeded = resolveDefaultSchemaCompareOptions(sourceEngine);
      seededDepthRef.current = depthKeysOf(seeded);
      return seeded;
    });
  }, [open, initialOptions, sourceEngine]);

  /**
   * MERGE (F07 seed x W5.2 saved defaults) — re-derive the DEPTH keys when the
   * source engine changes, but only while the user has not chosen a depth.
   *
   * `applyStandardDepthFallback` is a no-op the moment `detailed` or
   * `ignoredProperties` is defined, and the seed above always defines both. So
   * once the dialog had been seeded for engine A, switching to engine B left
   * A's depth keys in place and B's own saved default could never assert
   * itself — the exact "fallback-derived depth keys forcing Deep through the
   * merge" that `SchemaCompareSetupDialog.defaultDepth.test.tsx`'s F-2 case
   * pins. Neither branch is wrong on its own; only their composition is.
   *
   * The discriminator is the seed itself: `seededDepthRef` holds the depth keys
   * this dialog last WROTE, so "still equal to it" means the user has not
   * touched Depth, and any non-depth edit (the F-2 case unticks Views first) is
   * irrelevant and survives untouched.
   */
  useEffect(() => {
    if (!open || initialOptions || !sourceEngine) return;
    setOptions((prev) => {
      const seededDepth = seededDepthRef.current;
      if (!seededDepth || !sameDepthKeys(depthKeysOf(prev), seededDepth)) return prev;
      const next = depthKeysOf(resolveDefaultSchemaCompareOptions(sourceEngine));
      if (sameDepthKeys(next, seededDepth)) return prev;
      seededDepthRef.current = next;
      return { ...prev, detailed: next.detailed, ignoredProperties: next.ignoredProperties };
    });
  }, [open, initialOptions, sourceEngine]);

  /**
   * F07 fix-round-1 — drop `ignoredProperties` the CURRENT engine cannot emit.
   *
   * `ignoredProperties` is engine-scoped (`ENGINE_PROPERTY_ALLOWLIST`). Seed the
   * dialog from an MSSQL default (`fillFactor`, `identitySeed`, …) and then
   * switch the source connection to PostgreSQL and those keys would ride along
   * — while `IgnoredPropertiesSection` renders the checklist filtered by the
   * NEW engine's allowlist, so they are invisible and impossible to untick.
   * Three consequences, all silent: `applyEngineDefaults` does not strip them,
   * `inferDepth` compares against the pgsql standard list and reports a
   * spurious "Custom" depth, and `handleOpenCompare` would persist MSSQL keys
   * under `pgsql`.
   *
   * The within-session leak predates F07, but seeding made it reachable with
   * zero user action and persistence made it durable — so it is closed here.
   * The write side of the seam applies the same filter unconditionally, since
   * a UI effect cannot be the only guard on a stored invariant.
   */
  useEffect(() => {
    if (!open || !sourceEngine) return;
    const allowed = new Set(ENGINE_PROPERTY_ALLOWLIST[sourceEngine]);
    setOptions((prev) => {
      const current = prev.ignoredProperties;
      if (!current || current.length === 0) return prev;
      const kept = current.filter((key) => allowed.has(key));
      return kept.length === current.length ? prev : { ...prev, ignoredProperties: kept };
    });
  }, [open, sourceEngine]);

  // Fetch databases for source when connection changes
  useEffect(() => {
    // C2 (codex pre-PR review) — same normalise-on-early-return as the schemas
    // effect below. This one also covers the dialog-reopen race: the reset
    // effect above clears `connectionId`, but its state update lands after
    // ALL effects in that commit have run, so this effect still fires for the
    // PREVIOUS connection, sets the flag, and is then cancelled by the
    // dependency change — leaving `isLoadingDatabases: true` on an endpoint
    // with no connection at all, which kept Swap disabled forever.
    if (!source.connectionId || !open) {
      setSource((prev) => (prev.isLoadingDatabases ? { ...prev, isLoadingDatabases: false } : prev));
      return;
    }

    let cancelled = false;

    async function fetchDatabases() {
      setSource((prev) => ({ ...prev, isLoadingDatabases: true, databasesPhase: undefined }));
      try {
        // Soporte C3 — phase reports drive the field label; they never affect
        // the result, so a failure in either phase still lands in the C2
        // error surface below.
        // Direct call, not the shared cache — see the `getDatabases` import note.
        const result = await getDatabases(source.connectionId!, sourceEngine, {
          onPhase: (phase) => {
            if (!cancelled) setSource((prev) => ({ ...prev, databasesPhase: phase }));
          },
        });
        if (cancelled) return;
        setSource((prev) => ({
          ...prev,
          // Slice 5b: only render the array as authoritative on `'ok'`.
          databases: result.state === 'ok' ? result.databases : [],
          databasesState: result.state,
          isLoadingDatabases: false,
          databasesPhase: undefined,
        }));
      } catch {
        if (!cancelled) {
          setSource((prev) => ({
            ...prev,
            databases: [],
            databasesState: 'error',
            isLoadingDatabases: false,
            databasesPhase: undefined,
          }));
        }
      }
    }

    fetchDatabases();
    return () => { cancelled = true; };
  }, [source.connectionId, sourceEngine, open]);

  // Fetch databases for target when connection changes
  useEffect(() => {
    // C2 — see the source-side comment above.
    if (!target.connectionId || !open) {
      setTarget((prev) => (prev.isLoadingDatabases ? { ...prev, isLoadingDatabases: false } : prev));
      return;
    }

    let cancelled = false;

    async function fetchDatabases() {
      setTarget((prev) => ({ ...prev, isLoadingDatabases: true, databasesPhase: undefined }));
      try {
        // Soporte C3 — see the source-side comment above.
        // Direct call, not the shared cache — see the `getDatabases` import note.
        const result = await getDatabases(target.connectionId!, targetEngine, {
          onPhase: (phase) => {
            if (!cancelled) setTarget((prev) => ({ ...prev, databasesPhase: phase }));
          },
        });
        if (cancelled) return;
        setTarget((prev) => ({
          ...prev,
          databases: result.state === 'ok' ? result.databases : [],
          databasesState: result.state,
          isLoadingDatabases: false,
          databasesPhase: undefined,
        }));
      } catch {
        if (!cancelled) {
          setTarget((prev) => ({
            ...prev,
            databases: [],
            databasesState: 'error',
            isLoadingDatabases: false,
            databasesPhase: undefined,
          }));
        }
      }
    }

    fetchDatabases();
    return () => { cancelled = true; };
  }, [target.connectionId, targetEngine, open]);

  // Fetch schemas for source when database changes
  useEffect(() => {
    // C2 (codex pre-PR review) — normalise the flag on EVERY early return.
    // The cleanup below only flips its local `cancelled`, so a listing that
    // was in flight when the user changed the selection leaves
    // `isLoadingSchemas: true` riding the `...prev` spread in the selection
    // handlers. If this run then bails (SQLite has no schemas; or no database
    // is picked yet) nothing else would ever clear it — "Loading schemas…"
    // and a disabled Edit…/Swap would persist until the dialog is reopened.
    // The identity guard keeps this from looping.
    if (!source.connectionId || !source.database || !open || isSqliteSource) {
      setSource((prev) => (prev.isLoadingSchemas ? { ...prev, isLoadingSchemas: false } : prev));
      return;
    }

    let cancelled = false;

    async function fetchSchemas() {
      setSource((prev) => ({ ...prev, isLoadingSchemas: true, schemasError: undefined }));
      try {
        // Use getTables to extract unique schemas
        const result = await getTables(source.connectionId!, source.database!, sourceEngine);
        if (cancelled) return;

        const uniqueSchemas = Array.from(new Set(result.tables.map((t) => t.schema))).sort();

        setSource((prev) => ({
          ...prev,
          schemas: uniqueSchemas,
          isLoadingSchemas: false,
          schemasError: result.error,
        }));
      } catch (err) {
        if (!cancelled) {
          setSource((prev) => ({
            ...prev,
            schemas: [],
            isLoadingSchemas: false,
            schemasError: err instanceof Error ? err.message : 'Failed to load schemas',
          }));
        }
      }
    }

    fetchSchemas();
    return () => { cancelled = true; };
  }, [source.connectionId, source.database, sourceEngine, open, isSqliteSource]);

  // Fetch schemas for target when database changes
  useEffect(() => {
    // C2 — see the source-side comment above.
    if (!target.connectionId || !target.database || !open || isSqliteTarget) {
      setTarget((prev) => (prev.isLoadingSchemas ? { ...prev, isLoadingSchemas: false } : prev));
      return;
    }

    let cancelled = false;

    async function fetchSchemas() {
      setTarget((prev) => ({ ...prev, isLoadingSchemas: true, schemasError: undefined }));
      try {
        // Use getTables to extract unique schemas
        const result = await getTables(target.connectionId!, target.database!, targetEngine);
        if (cancelled) return;

        const uniqueSchemas = Array.from(new Set(result.tables.map((t) => t.schema))).sort();

        setTarget((prev) => ({
          ...prev,
          schemas: uniqueSchemas,
          isLoadingSchemas: false,
          schemasError: result.error,
        }));
      } catch (err) {
        if (!cancelled) {
          setTarget((prev) => ({
            ...prev,
            schemas: [],
            isLoadingSchemas: false,
            schemasError: err instanceof Error ? err.message : 'Failed to load schemas',
          }));
        }
      }
    }

    fetchSchemas();
    return () => { cancelled = true; };
  }, [target.connectionId, target.database, targetEngine, open, isSqliteTarget]);

  // Handlers
  // C2 (codex pre-PR review) — every handler that changes an endpoint's
  // connection/database hands the schemas fetch effect a new identity, so the
  // in-flight listing for the OLD identity stops owning `isLoadingSchemas`.
  // Reset it here rather than inheriting it through `...prev`; the effect
  // sets it back to true on the very next commit whenever it actually runs.
  // (`isLoadingDatabases` deliberately stays untouched: its effect's deps
  // always change on a connection pick, so it is always re-owned.)
  const handleSourceConnectionChange = useCallback((connectionId: string) => {
    setSource((prev) => ({
      ...prev,
      connectionId,
      database: getDatabaseFromConnectionId(connectionId, connections, activeConnections),
      databases: [],
      schemas: [],
      schemasError: undefined,
      isLoadingSchemas: false,
      objectType: undefined,
      objectSchema: undefined,
      objectName: undefined,
    }));
  }, [connections, activeConnections]);

  const handleSourceDatabaseChange = useCallback((database: string) => {
    setSource((prev) => ({
      ...prev,
      database,
      schemas: [],
      schemasError: undefined,
      isLoadingSchemas: false,
      objectType: undefined,
      objectSchema: undefined,
      objectName: undefined,
    }));
  }, []);

  const handleTargetConnectionChange = useCallback((connectionId: string) => {
    setTarget((prev) => ({
      ...prev,
      connectionId,
      database: getDatabaseFromConnectionId(connectionId, connections, activeConnections),
      databases: [],
      schemas: [],
      schemasError: undefined,
      isLoadingSchemas: false,
      objectType: undefined,
      objectSchema: undefined,
      objectName: undefined,
    }));
  }, [connections, activeConnections]);

  const handleTargetDatabaseChange = useCallback((database: string) => {
    setTarget((prev) => ({
      ...prev,
      database,
      schemas: [],
      schemasError: undefined,
      isLoadingSchemas: false,
      objectType: undefined,
      objectSchema: undefined,
      objectName: undefined,
    }));
  }, []);

  // Object selection handlers
  const handleSourceObjectChange = useCallback((value: ObjectPickerValue) => {
    setSource((prev) => ({
      ...prev,
      objectType: value.objectType,
      objectSchema: value.objectSchema,
      objectName: value.objectName,
    }));

    // Auto-select matching object on target if source object is selected
    if (value.objectType && value.objectSchema && value.objectName) {
      setTarget((prev) => {
        // Only auto-select if target doesn't have an object selected yet
        if (!prev.objectType) {
          return {
            ...prev,
            objectType: value.objectType,
            objectSchema: value.objectSchema,
            objectName: value.objectName,
          };
        }
        return prev;
      });
    }
  }, []);

  const handleTargetObjectChange = useCallback((value: ObjectPickerValue) => {
    setTarget((prev) => ({
      ...prev,
      objectType: value.objectType,
      objectSchema: value.objectSchema,
      objectName: value.objectName,
    }));
  }, []);

  const handleSelectRecent = useCallback((entry: RecentSchemaCompareEntry) => {
    // W5.1 (F02) — a recent pick owns the options decision for this session;
    // never let the saved-per-engine-defaults auto-seed effect below fire
    // afterwards and override what the user just explicitly picked.
    optionsSeededRef.current = true;
    setCompareWholeDatabase(entry.compareWholeDatabase);
    // Recent entries pre-date the unified filter — recover from whichever side has one.
    setSchemaFilter(entry.source.schemaFilter ?? entry.target.schemaFilter);
    // v1 entries (W5.1) snapshot options — restore them. Entries persisted
    // before v1 simply lack the field: leave the dialog's current options
    // untouched, which is exactly pre-W5.1 behaviour (never crashes, never
    // applies a half-populated options object).
    if (entry.options) {
      setOptions(entry.options);
      track('feature:schema_compare_recent_restore_options');
    }
    setSource((prev) => restoreEndpointFromRecent(prev, entry.source));
    setTarget((prev) => restoreEndpointFromRecent(prev, entry.target));
  }, [track]);

  // W5.1 (D4) — "every new compare starts from your preferences": once a
  // fresh dialog session (no user-chosen `initialOptions`, no recent picked
  // yet) resolves an engine for the first time, seed `options` from that
  // engine's saved default.
  //
  // M1 (fix round 1): only latch `optionsSeededRef` once a save is actually
  // applied. An engine with nothing saved must NOT permanently disarm this
  // effect — e.g. picking a SQLite source (no saved default) and then
  // switching to a MySQL source that has one must still seed it in the same
  // open session. Re-runs on every `sourceEngine`/`targetEngine` change
  // until a save is found or the dialog closes.
  //
  // M2 (fix round 1): when the caller marked `initialOptions` as NOT
  // user-chosen (`optionsAreUserChosen === false` — a factory-baseline
  // object, e.g. from a navigation-created tab that never opened this
  // dialog), a found saved default REPLACES it outright. Otherwise `prev`
  // wins over `saved` in the merge, so any option the user already dialed in
  // during the split-second before this fires is never clobbered.
  useEffect(() => {
    if (!open || optionsSeededRef.current) return;
    const engine = sourceEngine ?? targetEngine;
    if (!engine) return;
    const saved = getSchemaCompareDefaultOptions(engine);
    if (!saved) return;
    optionsSeededRef.current = true;
    if (initialOptions && optionsAreUserChosen === false) {
      setOptions(saved);
    } else {
      setOptions((prev) => ({ ...saved, ...prev }));
    }
  }, [open, sourceEngine, targetEngine, initialOptions, optionsAreUserChosen]);

  // C4 (Soporte fixes round 2, Wave C) — parity fix with
  // `DataCompareSetupDialog.tsx`'s identical `handleSwap`: when both sides
  // share the same connection (and/or database), swapping doesn't change
  // that slot's fetch-effect dependencies, so the effect never re-runs — a
  // moved-in `isLoadingDatabases`/`isLoadingSchemas: true` flag pends
  // forever (Retry hidden, gated on `!isLoading*`), and an in-flight fetch
  // for the OTHER (still-owning) slot can later clobber the freshly-
  // swapped-in endpoint's loaded schemas/error state. Disable swap while
  // any of the four loading flags are true — `isAnyEndpointLoading` also
  // disables the button in the UI below.
  const isAnyEndpointLoading =
    source.isLoadingDatabases ||
    source.isLoadingSchemas ||
    target.isLoadingDatabases ||
    target.isLoadingSchemas;

  const handleSwap = useCallback(() => {
    if (isAnyEndpointLoading) return;
    const sourceSnapshot = { ...source };
    const targetSnapshot = { ...target };
    setSource(targetSnapshot);
    setTarget(sourceSnapshot);
  }, [source, target, isAnyEndpointLoading]);

  // Soporte C2 — Retry handlers for the inline listing-failure surfaces below.
  // Mirror the corresponding fetch effects above rather than sharing code
  // with them, since the effects are keyed to automatic dependency changes
  // (connection/database) while these are explicit user-initiated re-fetches.
  // Each handler guards its state writes against `prev.connectionId`
  // (and `prev.database`, for the schema variant) so a stale in-flight
  // retry — the user clicked Retry then switched connection/database
  // before it resolved — can't clobber the new endpoint's state. Mirrors
  // the `cancelled` flag the automatic effects use, adapted for a one-shot
  // user-triggered call that has no cleanup function to hook.
  const retrySourceDatabases = useCallback(() => {
    if (!source.connectionId) return;
    track('feature:compare_setup_retry', { dialog: 'schema', target: 'databases' });
    const connectionId = source.connectionId;
    setSource((prev) =>
      prev.connectionId !== connectionId
        ? prev
        : { ...prev, isLoadingDatabases: true, databasesPhase: undefined }
    );
    // Retry = a fresh in-flight listing by definition; never the shared cache.
    getDatabases(connectionId, sourceEngine, {
      onPhase: (phase) =>
        setSource((prev) =>
          prev.connectionId !== connectionId ? prev : { ...prev, databasesPhase: phase }
        ),
    })
      .then((result) => {
        setSource((prev) =>
          prev.connectionId !== connectionId
            ? prev
            : {
                ...prev,
                databases: result.state === 'ok' ? result.databases : [],
                databasesState: result.state,
                isLoadingDatabases: false,
                databasesPhase: undefined,
              }
        );
      })
      .catch(() => {
        setSource((prev) =>
          prev.connectionId !== connectionId
            ? prev
            : {
                ...prev,
                databases: [],
                databasesState: 'error',
                isLoadingDatabases: false,
                databasesPhase: undefined,
              }
        );
      });
  }, [source.connectionId, sourceEngine, track]);

  const retryTargetDatabases = useCallback(() => {
    if (!target.connectionId) return;
    track('feature:compare_setup_retry', { dialog: 'schema', target: 'databases' });
    const connectionId = target.connectionId;
    setTarget((prev) =>
      prev.connectionId !== connectionId
        ? prev
        : { ...prev, isLoadingDatabases: true, databasesPhase: undefined }
    );
    // Retry = a fresh in-flight listing by definition; never the shared cache.
    getDatabases(connectionId, targetEngine, {
      onPhase: (phase) =>
        setTarget((prev) =>
          prev.connectionId !== connectionId ? prev : { ...prev, databasesPhase: phase }
        ),
    })
      .then((result) => {
        setTarget((prev) =>
          prev.connectionId !== connectionId
            ? prev
            : {
                ...prev,
                databases: result.state === 'ok' ? result.databases : [],
                databasesState: result.state,
                isLoadingDatabases: false,
                databasesPhase: undefined,
              }
        );
      })
      .catch(() => {
        setTarget((prev) =>
          prev.connectionId !== connectionId
            ? prev
            : {
                ...prev,
                databases: [],
                databasesState: 'error',
                isLoadingDatabases: false,
                databasesPhase: undefined,
              }
        );
      });
  }, [target.connectionId, targetEngine, track]);

  const retrySourceSchemas = useCallback(() => {
    if (!source.connectionId || !source.database) return;
    track('feature:compare_setup_retry', { dialog: 'schema', target: 'tables' });
    const connectionId = source.connectionId;
    const database = source.database;
    const isStale = (prev: EndpointState) => prev.connectionId !== connectionId || prev.database !== database;
    setSource((prev) => (isStale(prev) ? prev : { ...prev, isLoadingSchemas: true, schemasError: undefined }));
    getTables(connectionId, database, sourceEngine)
      .then((result) => {
        const uniqueSchemas = Array.from(new Set(result.tables.map((t) => t.schema))).sort();
        setSource((prev) =>
          isStale(prev)
            ? prev
            : { ...prev, schemas: uniqueSchemas, isLoadingSchemas: false, schemasError: result.error }
        );
      })
      .catch((err) => {
        setSource((prev) =>
          isStale(prev)
            ? prev
            : {
                ...prev,
                schemas: [],
                isLoadingSchemas: false,
                schemasError: err instanceof Error ? err.message : 'Failed to load schemas',
              }
        );
      });
  }, [source.connectionId, source.database, sourceEngine, track]);

  const retryTargetSchemas = useCallback(() => {
    if (!target.connectionId || !target.database) return;
    track('feature:compare_setup_retry', { dialog: 'schema', target: 'tables' });
    const connectionId = target.connectionId;
    const database = target.database;
    const isStale = (prev: EndpointState) => prev.connectionId !== connectionId || prev.database !== database;
    setTarget((prev) => (isStale(prev) ? prev : { ...prev, isLoadingSchemas: true, schemasError: undefined }));
    getTables(connectionId, database, targetEngine)
      .then((result) => {
        const uniqueSchemas = Array.from(new Set(result.tables.map((t) => t.schema))).sort();
        setTarget((prev) =>
          isStale(prev)
            ? prev
            : { ...prev, schemas: uniqueSchemas, isLoadingSchemas: false, schemasError: result.error }
        );
      })
      .catch((err) => {
        setTarget((prev) =>
          isStale(prev)
            ? prev
            : {
                ...prev,
                schemas: [],
                isLoadingSchemas: false,
                schemasError: err instanceof Error ? err.message : 'Failed to load schemas',
              }
        );
      });
  }, [target.connectionId, target.database, targetEngine, track]);

  const handleOpenCompare = useCallback(() => {
    // Unified schema filter applies to both endpoints (only in whole-database mode).
    const filterToApply =
      compareWholeDatabase && schemaFilter && schemaFilter.schemas.length > 0
        ? schemaFilter
        : undefined;

    // Build endpoint objects
    const sourceEndpoint: SchemaCompareEndpoint = {
      connectionId: source.connectionId,
      database: source.database,
      ...(filterToApply ? { schemaFilter: filterToApply } : {}),
      // Include object selection if in single-object mode
      ...(!compareWholeDatabase && source.objectType ? {
        objectType: source.objectType,
        objectSchema: source.objectSchema,
        objectName: source.objectName,
      } : {}),
    };

    const targetEndpoint: SchemaCompareEndpoint = {
      connectionId: target.connectionId,
      database: target.database,
      ...(filterToApply ? { schemaFilter: filterToApply } : {}),
      // Include object selection if in single-object mode
      ...(!compareWholeDatabase && target.objectType ? {
        objectType: target.objectType,
        objectSchema: target.objectSchema,
        objectName: target.objectName,
      } : {}),
    };

    // Snapshot display info for "Recently compared" history
    const sourceConn = connectedConnections.find((c) => c.id === source.connectionId);
    const targetConn = connectedConnections.find((c) => c.id === target.connectionId);

    // Materialize baseline defaults (ignoreCase=false, ignoreWhitespace=true)
    // plus the W5.2 Standard-depth fallback (effectiveOptions) so the
    // persisted options object reflects what the user actually SAW in the
    // dialog, not the pre-fallback `{}` that would silently submit as Deep.
    // User-explicit toggles are preserved either way.
    const finalOptions = applyEngineDefaults(effectiveOptions);

    // W5.1 (F02) — snapshot the fully-materialized options alongside the
    // endpoints so picking this entry later restores the whole compare
    // configuration, not just source/target.
    recordRecent({
      source: sourceEndpoint,
      target: targetEndpoint,
      compareWholeDatabase,
      options: finalOptions,
      sourceConnectionName: safeConnectionName(sourceConn),
      sourceServerHost: safeConnectionServer(sourceConn),
      targetConnectionName: safeConnectionName(targetConn),
      targetServerHost: safeConnectionServer(targetConn),
    });

    // F07 — remember what this comparison actually ran with, so the NEXT new
    // comparison on this engine starts here instead of at the hardcoded
    // baseline. This is the single choke point where the user's dialog
    // choices become a real compare, on both branches below (new tab and
    // "Change" on an existing tab).
    //
    // …with one carve-out. When this dialog was opened on a tab involving a
    // Database Blueprint folder, the object-type flags it was HANDED are a
    // SCOPE CAP read off the blueprint's manifest, not a preference the user
    // expressed: `openSchemaCompareWithFolderAsSource` switches off whatever
    // the blueprint does not model. Persisting those would make one
    // blueprint's scope the engine-wide default for every later unrelated
    // compare — the same class of bug F07 exists to fix, one layer up.
    //
    // The carve-out is PER FLAG, not whole-object. This code only runs behind
    // Open Compare, which is `disabled={!canOpen}`, and `canOpen` requires a
    // connection AND a database on BOTH endpoints — so by the time we get here
    // both sides are live databases and the object-type, depth and
    // ignored-property controls have been rendered and editable for the whole
    // dialog session. Discarding the lot would throw away choices the user did
    // see and could have changed. `rememberSchemaCompareOptions` therefore
    // compares each capped flag against what was handed in: unchanged means
    // inherited (leave the engine default alone), changed means chosen.
    //
    // WHICH flags are caps is READ, not re-derived (fix round 3). The tab
    // records them in `scopeCapKeys` when the blueprint route creates it, and
    // narrows the list on every confirm. Deriving it from endpoint kind — as
    // this did in round 2 — silently stops working the moment a folder tab is
    // re-pointed at two live databases: the caps are still in `options`, but
    // nothing in the tab's SHAPE says so any more, and the next confirm would
    // persist them.
    const inheritedCaps = pickInheritedCaps(initialOptions, initialScopeCapKeys, {
      folderEndpoint: isFolderEndpoint(initialSource) || isFolderEndpoint(initialTarget),
    });
    rememberSchemaCompareOptions(sourceEngine, finalOptions, inheritedCaps);

    // If onConfirm is provided, use it (update mode for existing tab)
    if (onConfirm) {
      onConfirm(
        sourceEndpoint,
        targetEndpoint,
        compareWholeDatabase,
        filterToApply,
        finalOptions,
      );
      onOpenChange(false);
      return;
    }

    // Create label based on mode
    let tabTitle: string;
    if (compareWholeDatabase) {
      const sourceLabel = source.database || sourceConn?.displayName || 'Source';
      const targetLabel = target.database || targetConn?.displayName || 'Target';
      tabTitle = `Schema: ${sourceLabel} → ${targetLabel}`;
    } else {
      // Single object mode - show object name
      const objectName = source.objectName || 'Object';
      tabTitle = `Compare: ${objectName}`;
    }

    addTab({
      title: tabTitle,
      type: 'schema-compare',
      content: {
        source: sourceEndpoint,
        target: targetEndpoint,
        compareWholeDatabase,
        options: finalOptions,
      },
    });

    onOpenChange(false);
  }, [
    source,
    target,
    compareWholeDatabase,
    schemaFilter,
    effectiveOptions,
    sourceEngine,
    // Read by the inherited-caps carve-out above (fix rounds 1-3).
    initialSource,
    initialTarget,
    initialOptions,
    initialScopeCapKeys,
    connectedConnections,
    addTab,
    onOpenChange,
    onConfirm,
    recordRecent,
  ]);

  // Validation - require connection and database, and object if in single-object mode
  const canOpen = useMemo(() => {
    const hasSource = source.connectionId && source.database;
    const hasTarget = target.connectionId && target.database;

    if (!hasSource || !hasTarget) return false;

    // In single-object mode, require object selection on source
    if (!compareWholeDatabase) {
      const hasSourceObject = source.objectType && source.objectSchema && source.objectName;
      if (!hasSourceObject) return false;
    }

    return true;
  }, [source, target, compareWholeDatabase]);

  // Check if target object is missing (for info message)
  const targetObjectMissing = useMemo(() => {
    if (compareWholeDatabase) return false;
    if (!source.objectType || !source.objectSchema || !source.objectName) return false;
    if (!target.connectionId || !target.database) return false;
    return !target.objectType || !target.objectSchema || !target.objectName;
  }, [source, target, compareWholeDatabase]);

  // Should the unified Schemas section be visible?
  // - In whole-database mode only
  // - At least one side is non-sqlite (sqlite has no schemas)
  // - Both endpoints chosen
  const showSchemaSection =
    compareWholeDatabase &&
    !!source.database &&
    !!target.database &&
    !(isSqliteSource && isSqliteTarget);
  const isLoadingAnySchemas = source.isLoadingSchemas || target.isLoadingSchemas;

  // R3-4 — recents stop occupying the top of the dialog once the user has
  // committed to an endpoint; from then on they are one click away from the
  // header link instead of permanently pushing SOURCE/TARGET down.
  const recents = useRecentlyComparedDisclosure({
    hasSelection: !!source.connectionId || !!target.connectionId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="schema-compare-setup-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              Schema Compare Setup
            </DialogTitle>
            <div className="flex items-center gap-4">
              {/* R3-4 — collapsed recents live to the LEFT of the checkbox. */}
              {recents.showLink && (
                <RecentlyComparedLink open={recents.open} onToggle={recents.toggle} />
              )}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="compare-whole-database"
                  checked={compareWholeDatabase}
                  onCheckedChange={(checked) => setCompareWholeDatabase(checked === true)}
                  data-testid="compare-whole-database-checkbox"
                />
                <label
                  htmlFor="compare-whole-database"
                  className="text-sm font-medium cursor-pointer"
                >
                  Compare whole database
                </label>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">

        {/* F11 — recents is an input, not a footer note: picking an entry
            prefills the panels below, so it sits above the form it feeds.
            That is also what puts "Open Compare" under the configuration.
            R3-4 — but only while it is still the fastest way in; once an
            endpoint is chosen it collapses to the header link above. */}
        {recents.open && (
          <div className="pb-4">
            <RecentlyComparedSection onSelect={handleSelectRecent} />
          </div>
        )}

        <div className="flex gap-3 items-start py-3">
          {/* Source Panel */}
          <div className="flex-1 border rounded-lg p-3 space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Source
            </h3>

            <SchemaCompareCompactField
              icon={<Server className="h-3.5 w-3.5" />}
              placeholder="Select connection…"
              value={source.connectionId ?? ''}
              displayLabel={connectionDisplayLabel(source.connectionId)}
              onValueChange={handleSourceConnectionChange}
              testId="source-connection-select"
            >
              {connectedConnections.length === 0 ? (
                <SelectItem value="_none" disabled>
                  No active connections
                </SelectItem>
              ) : (
                connectedConnections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    {safeConnectionName(conn)}
                    {conn.engine && conn.engine !== 'mssql' && (
                      <span className="ml-1 text-xs text-muted-foreground uppercase">
                        ({conn.engine})
                      </span>
                    )}
                  </SelectItem>
                ))
              )}
            </SchemaCompareCompactField>

            <SchemaCompareCompactField
              icon={<Database className="h-3.5 w-3.5" />}
              placeholder="Select database…"
              value={source.database ?? ''}
              onValueChange={handleSourceDatabaseChange}
              disabled={!source.connectionId}
              isLoading={source.isLoadingDatabases}
              loadingPhase={source.databasesPhase}
              testId="source-database-select"
            >
              <FallbackSelectContent
                connectionId={source.connectionId}
                state={source.databasesState}
                databases={source.databases}
                isLoading={source.isLoadingDatabases}
                selectedDatabase={source.database}
                testIdPrefix="source"
              />
            </SchemaCompareCompactField>

            {/* #91 — the restored database is not one this connection lists.
                Renders nothing in every other state; see
                `utils/unlistedDatabase.ts` for which states those are. */}
            <UnlistedDatabaseNotice
              connectionId={source.connectionId}
              database={source.database}
              databases={source.databases}
              state={source.databasesState}
              isLoading={source.isLoadingDatabases}
              testIdPrefix="source"
            />

            {/* Soporte C2 — database-listing failure, visible without opening
                the (closed) dropdown above. */}
            {source.databasesState === 'error' && !source.isLoadingDatabases && (
              <div className="flex flex-col gap-1.5">
                <DatabaseListingFallbackBanner
                  connectionId={source.connectionId!}
                  variant="row"
                  trigger="list_failure"
                  data-testid="source-databases-fallback-banner"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 self-start px-2 text-xs"
                  onClick={retrySourceDatabases}
                  data-testid="source-databases-retry"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Soporte C2 — schema-derivation listing failure (getTables). */}
            {!isSqliteSource && source.database && !source.isLoadingSchemas && source.schemasError && (
              <div
                className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive"
                data-testid="source-schemas-error"
              >
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Couldn&apos;t load schema info for this database.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  onClick={retrySourceSchemas}
                  data-testid="source-schemas-retry"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Object Picker - only in single-object mode */}
            {!compareWholeDatabase && source.database && (
              <SchemaCompareObjectPicker
                connectionId={source.connectionId}
                database={source.database}
                engineId={sourceEngine ?? 'mssql'}
                value={{
                  objectType: source.objectType,
                  objectSchema: source.objectSchema,
                  objectName: source.objectName,
                }}
                onChange={handleSourceObjectChange}
                testIdPrefix="source"
              />
            )}
          </div>

          {/* Swap Button */}
          <div className="flex items-center pt-6">
            <Tooltip>
              <TooltipTrigger asChild>
                {/* C4 — a disabled shadcn Button has `pointer-events-none`,
                    which blocks Radix's hover trigger entirely, so the
                    wrapping span carries the trigger role instead (mirrors
                    FixedValueDisabledButton.tsx's "why disabled" pattern). */}
                <span tabIndex={isAnyEndpointLoading ? 0 : -1}>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleSwap}
                    disabled={isAnyEndpointLoading}
                    data-testid="swap-button"
                    aria-label="Swap source and target"
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {isAnyEndpointLoading
                  ? 'Wait for lists to finish loading'
                  : 'Swap source and target'}
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Target Panel */}
          <div className="flex-1 border rounded-lg p-3 space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Target
            </h3>

            <SchemaCompareCompactField
              icon={<Server className="h-3.5 w-3.5" />}
              placeholder="Select connection…"
              value={target.connectionId ?? ''}
              displayLabel={connectionDisplayLabel(target.connectionId)}
              onValueChange={handleTargetConnectionChange}
              testId="target-connection-select"
            >
              {connectedConnections.length === 0 ? (
                <SelectItem value="_none" disabled>
                  No active connections
                </SelectItem>
              ) : (
                connectedConnections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    {safeConnectionName(conn)}
                    {conn.engine && conn.engine !== 'mssql' && (
                      <span className="ml-1 text-xs text-muted-foreground uppercase">
                        ({conn.engine})
                      </span>
                    )}
                  </SelectItem>
                ))
              )}
            </SchemaCompareCompactField>

            <SchemaCompareCompactField
              icon={<Database className="h-3.5 w-3.5" />}
              placeholder="Select database…"
              value={target.database ?? ''}
              onValueChange={handleTargetDatabaseChange}
              disabled={!target.connectionId}
              isLoading={target.isLoadingDatabases}
              loadingPhase={target.databasesPhase}
              testId="target-database-select"
            >
              <FallbackSelectContent
                connectionId={target.connectionId}
                state={target.databasesState}
                databases={target.databases}
                isLoading={target.isLoadingDatabases}
                selectedDatabase={target.database}
                testIdPrefix="target"
              />
            </SchemaCompareCompactField>

            {/* #91 — see the source-side note above. */}
            <UnlistedDatabaseNotice
              connectionId={target.connectionId}
              database={target.database}
              databases={target.databases}
              state={target.databasesState}
              isLoading={target.isLoadingDatabases}
              testIdPrefix="target"
            />

            {/* Soporte C2 — database-listing failure, visible without opening
                the (closed) dropdown above. */}
            {target.databasesState === 'error' && !target.isLoadingDatabases && (
              <div className="flex flex-col gap-1.5">
                <DatabaseListingFallbackBanner
                  connectionId={target.connectionId!}
                  variant="row"
                  trigger="list_failure"
                  data-testid="target-databases-fallback-banner"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 self-start px-2 text-xs"
                  onClick={retryTargetDatabases}
                  data-testid="target-databases-retry"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Soporte C2 — schema-derivation listing failure (getTables). */}
            {!isSqliteTarget && target.database && !target.isLoadingSchemas && target.schemasError && (
              <div
                className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive"
                data-testid="target-schemas-error"
              >
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Couldn&apos;t load schema info for this database.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  onClick={retryTargetSchemas}
                  data-testid="target-schemas-retry"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Object Picker - only in single-object mode */}
            {!compareWholeDatabase && target.database && (
              <SchemaCompareObjectPicker
                connectionId={target.connectionId}
                database={target.database}
                engineId={targetEngine ?? 'mssql'}
                value={{
                  objectType: target.objectType,
                  objectSchema: target.objectSchema,
                  objectName: target.objectName,
                }}
                onChange={handleTargetObjectChange}
                testIdPrefix="target"
              />
            )}

            {/* No target object picked.

                S3 (#43) — this used to say "Object not found in target", which
                was wrong twice over: the flag only means no target object has
                been PICKED (nothing has looked for it yet), and the promise
                that the comparison would merely "show it as added" was half
                the truth — the target was read in full, and everything on it
                came back as a removed row that Select All would happily take.
                Both sides are now narrowed to the same object whichever order
                the dialog was filled in, so this says what actually happens. */}
            {targetObjectMissing && (
              <div
                className="flex items-start gap-2 p-2 border rounded bg-muted/50 text-sm text-muted-foreground"
                data-testid="target-object-mirrored-note"
              >
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  No target object picked — Jam will compare only{' '}
                  <span className="font-medium text-foreground">
                    {source.objectSchema}.{source.objectName}
                  </span>{' '}
                  on both sides, and nothing else in the target database. If the target doesn't
                  have it, it shows as "added" in source. Pick a target object to compare against
                  a differently-named one.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Unified schema filter (applies to both source and target) */}
        {showSchemaSection && (
          <div className="pb-4">
            <SchemaFilterSection
              sourceSchemas={source.schemas}
              targetSchemas={target.schemas}
              isLoading={isLoadingAnySchemas}
              hasBothEndpoints={!!source.database && !!target.database}
              filter={schemaFilter}
              onFilterChange={setSchemaFilter}
              includeSystemSchemas={includeSystemSchemas}
              onIncludeSystemSchemasChange={setIncludeSystemSchemas}
            />
          </div>
        )}

        {/* Compare depth, object types, and advanced options.
            Mirrors the schema-filter section: only renders once both
            endpoints are picked, otherwise it's noise above an empty form. */}
        {!!source.database && !!target.database && (
          <div className="pb-4">
            <SchemaCompareInlineOptions
              options={effectiveOptions}
              onChange={setOptions}
              engine={engineForOptions}
              // R3-2 — `targetEngine`, NOT `engineForOptions`. Everything else
              // in this block is scoped by the SOURCE (which object types
              // exist, which properties are comparable); "Wrap in a single
              // transaction" is a property of the generated script and so
              // belongs to the endpoint the script runs against. On a
              // cross-engine comparison the two differ, and the tooltip is
              // engine-specific — a PostgreSQL target must never be described
              // in T-SQL because the source happens to be SQL Server.
              destinationEngine={targetEngine}
              singleObjectMode={!compareWholeDatabase}
              schemaFilter={schemaFilter}
              onSchemaFilterChange={setSchemaFilter}
            />
          </div>
        )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleOpenCompare} disabled={!canOpen} data-testid="open-compare-button">
            Open Compare
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
